import { ArrowRight } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { cn } from '@/shared/lib/utils';
import { Highlight } from '@/shared/ui/highlight';

import { SLIDE_FRAME, SlideEyebrow, SlideNumber } from './slide-parts';

type ProcessStep = {
  readonly order: string;
  readonly title: string;
  readonly body: string;
  /** 단계마다 붙는 확인 사항 한 줄 — 읽는 사람이 가장 먼저 묻는 것에 답한다 */
  readonly note: string;
};

/**
 * 교육 프로세스 — 문의에서 정착까지.
 *
 * 근거는 이 페이지에 이미 있는 말이다: 문의 후 진단·구성 안내(FAQ), 영업일 2일 안 답변(CTA),
 * 온라인 가능·첫 회차 대면 권장(FAQ), 코드 없이 진행(FAQ), 진단부터 정착까지(히어로).
 */
const STEPS: readonly ProcessStep[] = [
  {
    order: '01',
    title: '문의·진단',
    body: '어떤 업무에 시간이 드는지 듣고, 먼저 줄일 일을 함께 고릅니다.',
    note: '영업일 2일 안에 답변',
  },
  {
    order: '02',
    title: '맞춤 설계',
    body: '인원과 업무 범위에 맞춰 교육 구성과 일정을 제안합니다.',
    note: '대면·온라인 모두 가능',
  },
  {
    order: '03',
    title: '교육 진행',
    body: '우리 회사 실제 업무를 가지고 직접 만들어 봅니다.',
    note: '코드 없이 진행',
  },
  {
    order: '04',
    title: '정착 지원',
    body: '교육이 끝나도 자동화가 돌아가도록 운영까지 봅니다.',
    note: '담당자에게 이관',
  },
];

const LAST = STEPS.length - 1;

export function EducationProcess(): ReactElement {
  return (
    <section id="edu-process" aria-labelledby="edu-process-heading" className={SLIDE_FRAME}>
      <SlideNumber>03</SlideNumber>
      <div className="u-container flex flex-col gap-14 py-20 lg:py-16">
        <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
          <div className="flex flex-col items-start gap-6">
            <SlideEyebrow>Process · 교육 프로세스</SlideEyebrow>
            <h2
              id="edu-process-heading"
              className="text-[clamp(2rem,1.3rem+2.4vw,3.25rem)] font-bold leading-[1.22] tracking-[-0.05em]"
            >
              문의부터 정착까지,
              <br />
              <Highlight>4단계</Highlight>로 진행합니다
            </h2>
            <p className="max-w-[34rem] text-[16px] leading-[1.85] text-muted-foreground lg:text-[17px]">
              업무 범위와 인원을 먼저 듣고, 그에 맞춰 구성과 일정을 함께 정합니다.
            </p>
          </div>
          <Link
            to="/education/inquiry"
            className={cn(
              'group inline-flex h-14 shrink-0 items-center gap-2 rounded-full bg-ink-surface px-8 text-[16px] font-semibold text-on-ink',
              'transition-opacity hover:opacity-90',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
            )}
          >
            상담 신청하기
            <ArrowRight
              aria-hidden="true"
              className="size-4 transition-transform group-hover:translate-x-0.5"
              strokeWidth={2.2}
            />
          </Link>
        </div>

        {/* 번호 원을 가로지르는 선 — 네 단계가 한 흐름이라는 것을 보여준다(넓은 화면만) */}
        <ol className="relative grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <span
            aria-hidden="true"
            className="absolute top-[3.25rem] right-[12%] left-[12%] hidden h-px bg-foreground/15 lg:block"
          />
          {STEPS.map((step, index) => (
            <li
              key={step.order}
              className={cn(
                'group relative flex flex-col gap-5 rounded-[28px] p-7 transition duration-300 hover:-translate-y-1',
                index === LAST
                  ? 'bg-primary text-on-mint'
                  : 'border border-border bg-background hover:border-foreground',
              )}
            >
              <span
                className={cn(
                  'relative grid size-12 place-items-center rounded-full font-mono text-[15px] font-semibold',
                  index === LAST ? 'bg-on-mint text-primary' : 'bg-ink-surface text-on-ink',
                )}
              >
                {step.order}
              </span>
              <div className="flex flex-col gap-2">
                <h3 className="text-[20px] font-bold tracking-[-0.04em]">{step.title}</h3>
                <p
                  className={cn(
                    'text-[14.5px] leading-[1.7]',
                    index === LAST ? 'text-on-mint/80' : 'text-muted-foreground',
                  )}
                >
                  {step.body}
                </p>
              </div>
              <span
                className={cn(
                  'mt-auto w-fit rounded-full px-3 py-1 text-[12.5px] font-medium',
                  index === LAST ? 'bg-on-mint/10' : 'bg-mint-soft/60 text-on-mint',
                )}
              >
                {step.note}
              </span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
